// src/components/CharacterDashboard/CharacterDashboard.js
import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import Logo from '../Layout/Logo';
import styles from './CharacterDashboard.module.css';

const CharacterDashboard = ({ onCharacterSelect }) => {
  const { 
    currentUser, 
    userCharacters, 
    isLoadingCharacters, 
    createCharacter, 
    signOut,
    isGuest,
    deleteCharacter
  } = useContext(AuthContext);
  
  const [showCreateModalForSlot, setShowCreateModalForSlot] = useState(null);
  const [newCharacterName, setNewCharacterName] = useState('');
  const [selectedPortrait, setSelectedPortrait] = useState('');
  const [animatingSlot, setAnimatingSlot] = useState(null);

  // Available portraits
  const portraits = [
    { id: 'elara', name: 'Elara', gender: 'female', src: '/img/player/elara.jpeg' },
    { id: 'lyra', name: 'Lyra', gender: 'female', src: '/img/player/lyra.jpeg' },
    { id: 'maia', name: 'Maia', gender: 'female', src: '/img/player/maia.jpeg' },
    { id: 'emrys', name: 'Emrys', gender: 'male', src: '/img/player/emrys.jpeg' },
    { id: 'finn', name: 'Finn', gender: 'male', src: '/img/player/finn.jpeg' },
    { id: 'silas', name: 'Silas', gender: 'male', src: '/img/player/silas.jpeg' },
  ];

  useEffect(() => {
    console.log("[CharacterDashboard] State Update: isLoadingCharacters:", isLoadingCharacters, "userCharacters:", userCharacters, "currentUser:", !!currentUser, "isGuest:", isGuest);
  }, [isLoadingCharacters, userCharacters, currentUser, isGuest]);

  const handleAttemptCreateCharacter = async (slotId) => {
    if (!newCharacterName.trim()) {
      alert("Please enter a character name.");
      return;
    }
    if (!selectedPortrait) {
      alert("Please select a character portrait.");
      return;
    }
    console.log(`[CharacterDashboard] Calling createCharacter for slot ${slotId} with name "${newCharacterName}" and portrait "${selectedPortrait}"`);
    
    // Create character with portrait data
    const extraData = {
      portrait: selectedPortrait
    };
    
    const newCharData = await createCharacter(newCharacterName, slotId, extraData);
    if (newCharData) {
      console.log("[CharacterDashboard] Character creation successful, calling onCharacterSelect with:", newCharData);
      setAnimatingSlot(slotId);
      setTimeout(() => setAnimatingSlot(null), 1000);
      onCharacterSelect(newCharData); 
    } else {
      console.log("[CharacterDashboard] Character creation failed or returned null.");
    }
    setShowCreateModalForSlot(null); 
    setNewCharacterName(''); 
    setSelectedPortrait('');
  };
  
  const handlePlayCharacter = (characterData) => {
    if (characterData) {
      console.log("[CharacterDashboard] Playing character:", characterData);
      onCharacterSelect(characterData);
    }
  };

  const handleAttemptDeleteCharacter = async (characterSlotId, characterName) => {
    if (characterSlotId === undefined || characterSlotId < 0 || characterSlotId > 2) {
        alert("Invalid character slot for deletion.");
        return;
    }
    if (window.confirm(`Are you sure you want to delete character "${characterName}" in slot ${characterSlotId + 1}? This action cannot be undone.`)) {
      console.log(`[CharacterDashboard] Confirmed deletion for slot ${characterSlotId}`);
      const success = await deleteCharacter(characterSlotId);
      if (success) {
        alert(`Character "${characterName}" deleted successfully.`);
      } else {
        alert(`Failed to delete character "${characterName}". Please try again.`);
      }
    }
  };

  const getCharacterTitle = (character) => {
    const combatLevel = character.skills?.combat?.level || 1;
    if (combatLevel >= 50) return "Legendary Hero";
    if (combatLevel >= 30) return "Veteran Warrior";
    if (combatLevel >= 15) return "Skilled Adventurer";
    if (combatLevel >= 5) return "Novice Fighter";
    return "Fresh Recruit";
  };

  const renderCreateCharacterModal = () => {
    if (showCreateModalForSlot === null) return null;
    const slotId = showCreateModalForSlot;
    const selectedPortraitData = portraits.find(p => p.id === selectedPortrait);
    
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h2>⚔️ Create New Hero</h2>
            <h3>Character Slot {slotId + 1}</h3>
          </div>
          <div className={styles.modalBody}>
            <label className={styles.inputLabel}>Enter your hero's name:</label>
            <input
              type="text"
              placeholder="e.g., Brave Knight, Wise Mage..."
              value={newCharacterName}
              onChange={(e) => setNewCharacterName(e.target.value)}
              className={styles.modalInput}
              autoFocus
              maxLength={20}
            />
            
            <label className={styles.inputLabel}>Choose your hero's portrait:</label>
            <div className={styles.portraitGrid}>
              {portraits.map(portrait => (
                <div 
                  key={portrait.id}
                  className={`${styles.portraitOption} ${selectedPortrait === portrait.id ? styles.selected : ''}`}
                  onClick={() => setSelectedPortrait(portrait.id)}
                >
                  <img 
                    src={portrait.src} 
                    alt={portrait.name}
                    className={styles.portraitImage}
                  />
                  <span className={styles.portraitLabel}>{portrait.name}</span>
                </div>
              ))}
            </div>
            
            <div className={styles.characterPreview}>
              <div className={styles.previewPortrait}>
                {selectedPortraitData ? (
                  <img 
                    src={selectedPortraitData.src} 
                    alt={selectedPortraitData.name}
                    className={styles.previewPortraitImage}
                  />
                ) : (
                  <div className={styles.previewIcon}>👤</div>
                )}
              </div>
              <div className={styles.previewText}>
                <strong>{newCharacterName || "New Hero"}</strong>
                <p>Fresh Recruit</p>
              </div>
            </div>
          </div>
          <div className={styles.modalActions}>
            <button 
              onClick={() => handleAttemptCreateCharacter(slotId)} 
              className={`${styles.modalButton} ${styles.createButton}`}
              disabled={!newCharacterName.trim() || !selectedPortrait}
            >
              ⚔️ Forge Hero
            </button>
            <button 
              onClick={() => { 
                setShowCreateModalForSlot(null); 
                setNewCharacterName(''); 
                setSelectedPortrait('');
              }} 
              className={`${styles.modalButton} ${styles.cancelButton}`}
            >
              ✕ Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (currentUser && !isGuest && isLoadingCharacters) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading your heroes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      {renderCreateCharacterModal()}
      
      <div className={styles.header}>
        <Logo />
        {currentUser && !isGuest && (
          <button onClick={signOut} className={styles.signOutButton}>
            <span>👋</span> Sign Out
          </button>
        )}
      </div>
      
      <div className={styles.characterSlots}>
        {(userCharacters || [null, null, null]).map((character, index) => (
          <div 
            key={index} 
            className={`${styles.characterSlot} ${animatingSlot === index ? styles.animating : ''}`}
          >
            <div className={styles.slotHeader}>
              <h3>Slot {index + 1}</h3>
              <div className={styles.slotDecoration}></div>
            </div>
            
            {character ? (
              <div className={styles.characterCard}>
                <div className={styles.characterPortrait}>
                  {character.portrait ? (
                    <img 
                      src={`/img/player/${character.portrait}.jpeg`} 
                      alt={character.name}
                      className={styles.portraitImage}
                    />
                  ) : (
                    <div className={styles.defaultPortrait}>👤</div>
                  )}
                </div>
                <div className={styles.characterInfo}>
                  <h4 className={styles.characterName}>{character.name}</h4>
                  <p className={styles.characterTitle}>{getCharacterTitle(character)}</p>
                  <div className={styles.characterStats}>
                    <div className={styles.statItem}>
                      <span className={styles.statIcon}>⚔️</span>
                      <span>Combat Level {character.skills?.combat?.level || 1}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statIcon}>💰</span>
                      <span>{character.gold || 0} gold</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statIcon}>📅</span>
                      <span>Day {character.day || 1}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.characterActions}>
                  <button 
                    onClick={() => handlePlayCharacter(character)} 
                    className={`${styles.actionButton} ${styles.playButton}`}
                  >
                    <span>⚔️</span> Adventure Awaits!
                  </button>
                  {currentUser && !isGuest && (
                    <button 
                      onClick={() => handleAttemptDeleteCharacter(character.slotId, character.name)} 
                      className={`${styles.actionButton} ${styles.deleteButton}`}
                    >
                      <span>🗑️</span> Delete
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.emptySlot}>
                <div className={styles.emptyIcon}>👤</div>
                <p className={styles.emptyText}>No hero forged yet</p>
                <button 
                  onClick={() => {
                    console.log(`[CharacterDashboard] Opening create modal for slot ${index}`);
                    setShowCreateModalForSlot(index);
                  }} 
                  className={`${styles.actionButton} ${styles.createButton}`}
                  disabled={!currentUser || isGuest}
                >
                  <span>⚒️</span> Forge New Hero
                </button>
                {(!currentUser || isGuest) && (
                  <p className={styles.disabledText}>Sign in to create characters</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className={styles.footer}>
        <p>🌟 Begin your journey in the realm of Ember & Anvil 🌟</p>
      </div>
    </div>
  );
};

export default CharacterDashboard;